import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Clock3,
  Edit2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Save,
  Search,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import { AddressFields } from '../../components/AddressFields';

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
type DaySchedule = { open: boolean; start: string | null; end: string | null };
type OpeningHours = Record<DayKey, DaySchedule>;

type LocationDetailsForm = {
  contactName: string;
  parkingInfo: string;
  accessInstructions: string;
  directions: string;
  notes: string;
  services: string;
  billingNpi: string;
  billingTaxId: string;
};

type LocationForm = {
  id?: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  formattedAddress: string;
  googlePlaceId: string;
  latitude: string;
  longitude: string;
  source: string;
  timezone: string;
  contactPhone: string;
  contactEmail: string;
  facilityType: string;
  telemedEnabled: boolean;
  isPrimary: boolean;
  active: boolean;
  openingHours: OpeningHours;
  details: LocationDetailsForm;
};

type LocationApi = Omit<LocationForm, 'details'> & {
  id: string;
  details: {
    contactName?: string;
    parkingInfo?: string;
    accessInstructions?: string;
    directions?: string;
    notes?: string;
    services?: string[];
    billingNpi?: string;
    billingTaxId?: string;
  };
  primaryAddress?: {
    formattedAddress?: string | null;
    googlePlaceId?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    source?: string | null;
  } | null;
};

type LocationStaffAssignment = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  active: boolean;
  assigned: boolean;
  defaultLocationId: string | null;
  assignedLocationIds: string[];
  canAccessAllLocations: boolean;
};

const dayLabels: Record<DayKey, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const dayKeys: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const makeDay = (open = false, start: string | null = null, end: string | null = null): DaySchedule => ({ open, start, end });
const closedHours = (): OpeningHours => ({
  monday: makeDay(),
  tuesday: makeDay(),
  wednesday: makeDay(),
  thursday: makeDay(),
  friday: makeDay(),
  saturday: makeDay(),
  sunday: makeDay(),
});
const defaultHours = (): OpeningHours => ({
  monday: makeDay(true, '09:00', '17:00'),
  tuesday: makeDay(true, '09:00', '17:00'),
  wednesday: makeDay(true, '09:00', '17:00'),
  thursday: makeDay(true, '09:00', '17:00'),
  friday: makeDay(true, '09:00', '17:00'),
  saturday: makeDay(),
  sunday: makeDay(),
});

const emptyForm = (): LocationForm => ({
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  formattedAddress: '',
  googlePlaceId: '',
  latitude: '',
  longitude: '',
  source: 'MANUAL',
  timezone: 'UTC',
  contactPhone: '',
  contactEmail: '',
  facilityType: '',
  telemedEnabled: false,
  isPrimary: false,
  active: true,
  openingHours: defaultHours(),
  details: {
    contactName: '',
    parkingInfo: '',
    accessInstructions: '',
    directions: '',
    notes: '',
    services: '',
    billingNpi: '',
    billingTaxId: '',
  },
});

const phonePattern = /^[0-9+().\s-]{7,}$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const showPrefill = import.meta.env.DEV;

function toForm(location: LocationApi): LocationForm {
  return {
    id: location.id,
    name: location.name || '',
    addressLine1: location.addressLine1 || '',
    addressLine2: location.addressLine2 || '',
    city: location.city || '',
    state: location.state || '',
    postalCode: location.postalCode || '',
    country: location.country || 'US',
    formattedAddress: location.primaryAddress?.formattedAddress || '',
    googlePlaceId: location.primaryAddress?.googlePlaceId || '',
    latitude: location.primaryAddress?.latitude != null ? String(location.primaryAddress.latitude) : '',
    longitude: location.primaryAddress?.longitude != null ? String(location.primaryAddress.longitude) : '',
    source: location.primaryAddress?.source || (location.primaryAddress?.googlePlaceId ? 'GOOGLE' : 'MANUAL'),
    timezone: location.timezone || 'UTC',
    contactPhone: location.contactPhone || '',
    contactEmail: location.contactEmail || '',
    facilityType: location.facilityType || '',
    telemedEnabled: !!location.telemedEnabled,
    isPrimary: !!location.isPrimary,
    active: location.active !== false,
    openingHours: location.openingHours || closedHours(),
    details: {
      contactName: location.details?.contactName ?? '',
      parkingInfo: location.details?.parkingInfo ?? '',
      accessInstructions: location.details?.accessInstructions ?? '',
      directions: location.details?.directions ?? '',
      notes: location.details?.notes ?? '',
      services: (location.details?.services ?? []).join(', '),
      billingNpi: location.details?.billingNpi ?? '',
      billingTaxId: location.details?.billingTaxId ?? '',
    },
  };
}

function buildPayload(form: LocationForm) {
  const services = form.details.services
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  return {
    name: form.name.trim(),
    addressLine1: form.addressLine1.trim(),
    addressLine2: form.addressLine2.trim() || undefined,
    city: form.city.trim(),
    state: form.state.trim(),
    postalCode: form.postalCode.trim(),
    country: form.country.trim(),
    formattedAddress: form.formattedAddress.trim() || undefined,
    googlePlaceId: form.googlePlaceId.trim() || undefined,
    latitude: form.latitude.trim() ? Number(form.latitude) : undefined,
    longitude: form.longitude.trim() ? Number(form.longitude) : undefined,
    source: form.source || undefined,
    timezone: form.timezone.trim(),
    contactPhone: form.contactPhone.trim(),
    contactEmail: form.contactEmail.trim() || undefined,
    facilityType: form.facilityType.trim() || undefined,
    openingHours: form.openingHours,
    telemedEnabled: form.telemedEnabled,
    isPrimary: form.isPrimary,
    active: form.active,
    details: {
      contactName: form.details.contactName.trim() || undefined,
      parkingInfo: form.details.parkingInfo.trim() || undefined,
      accessInstructions: form.details.accessInstructions.trim() || undefined,
      directions: form.details.directions.trim() || undefined,
      notes: form.details.notes.trim() || undefined,
      services,
      billingNpi: form.details.billingNpi.trim() || undefined,
      billingTaxId: form.details.billingTaxId.trim() || undefined,
    },
  };
}

function validateLocation(form: LocationForm, t: (key: string) => string) {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = t('Location name is required');
  if (!form.addressLine1.trim()) errors.addressLine1 = t('Address line 1 is required');
  if (!form.city.trim()) errors.city = t('City is required');
  if (!form.state.trim()) errors.state = t('State / Province is required');
  if (!form.postalCode.trim()) errors.postalCode = t('Postal code is required');
  if (!form.country.trim()) errors.country = t('Country is required');
  if (!form.timezone.trim()) errors.timezone = t('Timezone is required');
  if (!form.contactPhone.trim() || !phonePattern.test(form.contactPhone)) {
    errors.contactPhone = t('Contact phone is required');
  }
  if (form.contactEmail && !emailPattern.test(form.contactEmail)) {
    errors.contactEmail = t('Enter a valid email');
  }

  const scheduleErrors: string[] = [];
  dayKeys.forEach((day) => {
    const slot = form.openingHours[day];
    if (slot?.open) {
      if (!slot.start || !slot.end) {
        scheduleErrors.push(`${t(dayLabels[day])} ${t('needs start and end times')}`);
      } else if (slot.start >= slot.end) {
        scheduleErrors.push(`${t(dayLabels[day])} ${t('end time must be after start')}`);
      }
    }
  });
  if (scheduleErrors.length) {
    errors.openingHours = scheduleErrors.join(' • ');
  }
  return errors;
}

export default function SettingsLocations() {
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { fetchJson, fetchApi } = useApiClient();
  const { t } = useI18n();

  const [locations, setLocations] = useState<LocationApi[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [staffAssignments, setStaffAssignments] = useState<LocationStaffAssignment[]>([]);
  const [staffSaving, setStaffSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(true);

  const [form, setForm] = useState<LocationForm>(emptyForm());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await fetchJson<LocationApi[]>('/locations?includeInactive=true');
        setLocations(data);
      } catch (err: any) {
        setFailure(err.message ?? t('Failed to load locations'));
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchJson]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return locations.filter((loc) => {
      if (!showInactive && loc.active === false) return false;
      return (
        loc.name.toLowerCase().includes(q) ||
        (loc.city || '').toLowerCase().includes(q) ||
        (loc.state || '').toLowerCase().includes(q)
      );
    });
  }, [locations, search, showInactive]);
  const selectedLocation = locationId && locationId !== 'new' ? locations.find((location) => location.id === locationId) || null : null;

  useEffect(() => {
    if (!selectedLocation?.id) {
      setStaffAssignments([]);
      return;
    }
    let active = true;
    fetchJson<LocationStaffAssignment[]>(`/locations/${selectedLocation.id}/staff`)
      .then((rows) => {
        if (active) setStaffAssignments(rows);
      })
      .catch(() => {
        if (active) setStaffAssignments([]);
      });
    return () => {
      active = false;
    };
  }, [fetchJson, selectedLocation?.id]);

  useEffect(() => {
    if (!locationId) {
      setForm(emptyForm());
      setErrors({});
      setFailure(null);
      setBanner(null);
      return;
    }
    if (locationId === 'new') {
      setForm(emptyForm());
      setErrors({});
      setFailure(null);
      setBanner(null);
      return;
    }
    const selected = locations.find((location) => location.id === locationId);
    if (selected) {
      setForm(toForm(selected));
      setErrors({});
      setFailure(null);
      setBanner(null);
    }
  }, [locationId, locations]);

  const handleFormChange = <K extends keyof LocationForm>(key: K, value: LocationForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  const handleDetailChange = (key: keyof LocationDetailsForm, value: string) => {
    setForm((prev) => ({ ...prev, details: { ...prev.details, [key]: value } }));
  };

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors = validateLocation(form, t);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    setFailure(null);
    setBanner(null);

    try {
      const payload = buildPayload(form);
      const path = form.id ? `/locations/${form.id}` : '/locations';
      const res = await fetchApi(path, {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const saved = (await res.json()) as LocationApi;
      setLocations((prev) =>
        prev.some((l) => l.id === saved.id) ? prev.map((l) => (l.id === saved.id ? saved : l)) : [saved, ...prev],
      );
      setBanner(t('Location saved'));
      navigate('/settings/locations', { replace: true });
    } catch (err: any) {
      setFailure(err.message ?? t('Unable to save location'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t('Deactivate this location?'))) return;
    try {
      const res = await fetchApi(`/locations/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Deactivate failed (${res.status})`);
      setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, active: false, isPrimary: false } : l)));
    } catch (err: any) {
      setFailure(err.message ?? t('Unable to deactivate location'));
    }
  }

  async function reactivateLocation(id: string) {
    try {
      const saved = await fetchJson<LocationApi>(`/locations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: true }),
      });
      setLocations((prev) => prev.map((l) => (l.id === id ? saved : l)));
      setBanner(t('Location reactivated'));
    } catch (err: any) {
      setFailure(err.message ?? t('Unable to reactivate location'));
    }
  }

  async function saveStaffAssignments(locationId: string, userIds: string[]) {
    setStaffSaving(true);
    try {
      await fetchJson(`/locations/${locationId}/staff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds }),
      });
      const rows = await fetchJson<LocationStaffAssignment[]>(`/locations/${locationId}/staff`);
      setStaffAssignments(rows);
      setBanner(t('Location staff assignment saved'));
    } catch (err: any) {
      setFailure(err.message ?? t('Unable to save staff assignment'));
    } finally {
      setStaffSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{t('Locations')}</h1>
            <p className="text-slate-500 text-sm">{t('Manage clinics, offices, and operating hours.')}</p>
          </div>
        </div>
        {!locationId && (
          <button onClick={() => navigate('/settings/locations/new')} className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft flex items-center gap-2">
            <Plus size={16} /> {t('Add Location')}
          </button>
        )}
      </div>

      {banner && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
          <CheckCircle2 size={16} /> {banner}
        </div>
      )}
      {failure && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-lg">
          <ShieldAlert size={16} /> {failure}
        </div>
      )}

      {locationId ? (
        <div className="space-y-6">
          <LocationEditorPage
            navigate={navigate}
            locationId={locationId}
            selectedLocation={selectedLocation}
            staffAssignments={staffAssignments}
            staffSaving={staffSaving}
            loading={loading}
            form={form}
            errors={errors}
            saving={saving}
            failure={failure}
            t={t}
            setForm={setForm}
            handleFormChange={handleFormChange}
            handleDetailChange={handleDetailChange}
            handleSave={handleSave}
            saveStaffAssignments={saveStaffAssignments}
            showPrefill={showPrefill}
          />
        </div>
      ) : (
        <div className="card">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <Search size={16} className="text-slate-400" />
            <input
              className="w-full outline-none text-sm"
              placeholder={t('Search locations...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="flex shrink-0 items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} className="accent-ink" />
              {t('Show inactive')}
            </label>
          </div>
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium">
              <tr>
                <th className="p-4">{t('Name')}</th>
                <th className="p-4">{t('Address')}</th>
                <th className="p-4">{t('Timezone')}</th>
                <th className="p-4">{t('Status')}</th>
                <th className="p-4 text-right">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" /> {t('Loading...')}
                    </span>
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500">
                    {t('No locations found.')}
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((loc) => (
                  <tr key={loc.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-ink">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-slate-400" />
                        <div>
                          <div>{loc.name || t('Untitled')}</div>
                          <div className="text-xs text-slate-500">{loc.facilityType}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">
                      {[loc.addressLine1, loc.addressLine2, loc.city, loc.state, loc.postalCode, loc.country].filter(Boolean).join(', ')}
                    </td>
                    <td className="p-4 text-slate-600">{loc.timezone}</td>
                    <td className="p-4">
                      <div className="flex gap-2 flex-wrap">
                        {loc.isPrimary && (
                          <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">{t('Primary')}</span>
                        )}
                        {loc.telemedEnabled && (
                          <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">{t('Telemed')}</span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${loc.active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
                          {loc.active === false ? t('Inactive') : t('Active')}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      <button className="p-1 text-slate-400 hover:text-ink" onClick={() => navigate(`/settings/locations/${loc.id}`)} aria-label={t('Edit location')}>
                        <Edit2 size={16} />
                      </button>
                      {loc.active === false ? (
                        <button className="p-1 text-slate-400 hover:text-emerald-600" onClick={() => void reactivateLocation(loc.id)} aria-label={t('Reactivate location')}>
                          <CheckCircle2 size={16} />
                        </button>
                      ) : (
                        <button className="p-1 text-slate-400 hover:text-rose-600" onClick={() => handleDelete(loc.id)} aria-label={t('Deactivate location')}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LocationEditorPage({
  navigate,
  locationId,
  selectedLocation,
  staffAssignments,
  staffSaving,
  loading,
  form,
  errors,
  saving,
  failure,
  t,
  setForm,
  handleFormChange,
  handleDetailChange,
  handleSave,
  saveStaffAssignments,
  showPrefill,
}: {
  navigate: ReturnType<typeof useNavigate>;
  locationId: string;
  selectedLocation: LocationApi | null;
  staffAssignments: LocationStaffAssignment[];
  staffSaving: boolean;
  loading: boolean;
  form: LocationForm;
  errors: Record<string, string>;
  saving: boolean;
  failure: string | null;
  t: (key: string) => string;
  setForm: React.Dispatch<React.SetStateAction<LocationForm>>;
  handleFormChange: <K extends keyof LocationForm>(key: K, value: LocationForm[K]) => void;
  handleDetailChange: (key: keyof LocationDetailsForm, value: string) => void;
  handleSave: (e: React.FormEvent) => Promise<void>;
  saveStaffAssignments: (locationId: string, userIds: string[]) => Promise<void>;
  showPrefill: boolean;
}) {
  const isNew = locationId === 'new';

  if (!isNew && !loading && !selectedLocation) {
    return (
      <div className="card p-6 text-slate-600">
        {t('Location not found.')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        {isNew && showPrefill ? (
          <button
            type="button"
            onClick={() =>
              setForm({
                name: 'Downtown Care Center',
                addressLine1: '123 Main St',
                addressLine2: 'Suite 400',
                city: 'Austin',
                state: 'TX',
                postalCode: '78701',
                country: 'US',
                formattedAddress: '123 Main St, Suite 400, Austin, TX 78701, US',
                googlePlaceId: 'example-place-id',
                latitude: '30.2672',
                longitude: '-97.7431',
                source: 'MANUAL',
                timezone: 'America/Chicago',
                contactPhone: '512-555-1200',
                contactEmail: 'frontdesk@downtowncare.test',
                facilityType: 'Clinic',
                telemedEnabled: true,
                isPrimary: true,
                active: true,
                openingHours: defaultHours(),
                details: {
                  contactName: 'Alex Morgan',
                  parkingInfo: 'Garage on 5th, validate at desk',
                  accessInstructions: 'Take elevator to 4th floor, check-in kiosk by entrance',
                  directions: 'Corner of 5th & Main, across from city library',
                  notes: 'Great for new patient intakes and follow-ups',
                  services: 'Primary Care, Labs, Imaging',
                  billingNpi: '1457392012',
                  billingTaxId: '12-3456789',
                },
              })
            }
            className="px-3 py-1 text-xs border border-dashed border-slate-300 rounded-lg text-slate-600 hover:text-ink hover:border-ink"
          >
            {t('Prefill demo data')}
          </button>
        ) : null}
      </div>

      {failure && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-lg">
          <ShieldAlert size={16} /> {failure}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-slate-500" />
            <h4 className="font-semibold text-ink">{t('Location basics')}</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label={t('Location Name')} required value={form.name} error={errors.name} onChange={(v) => handleFormChange('name', v)} />
                <Input label={t('Facility Type')} value={form.facilityType} onChange={(v) => handleFormChange('facilityType', v)} placeholder={t('Clinic, Office, Lab')} />
            <div className="md:col-span-2">
              <AddressFields
                value={{
                  line1: form.addressLine1,
                  line2: form.addressLine2,
                  city: form.city,
                  state: form.state,
                  postalCode: form.postalCode,
                  country: form.country,
                formattedAddress: form.formattedAddress,
                googlePlaceId: form.googlePlaceId,
                latitude: form.latitude ? Number(form.latitude) : null,
                longitude: form.longitude ? Number(form.longitude) : null,
                source: form.source as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
              }}
                onChange={(next) => {
                  handleFormChange('addressLine1', next.line1);
                  handleFormChange('addressLine2', next.line2);
                  handleFormChange('city', next.city);
                  handleFormChange('state', next.state);
                  handleFormChange('postalCode', next.postalCode);
                  handleFormChange('country', next.country);
                  handleFormChange('formattedAddress', next.formattedAddress ?? '');
                  handleFormChange('googlePlaceId', next.googlePlaceId ?? '');
                  handleFormChange('latitude', next.latitude != null ? String(next.latitude) : '');
                  handleFormChange('longitude', next.longitude != null ? String(next.longitude) : '');
                  handleFormChange('source', next.source ?? 'MANUAL');
                }}
                errors={{
                  line1: errors.addressLine1,
                  city: errors.city,
                  state: errors.state,
                  postalCode: errors.postalCode,
                  country: errors.country,
                }}
                labels={{
                  line1: t('Address Line 1'),
                  line2: t('Address Line 2'),
                  city: t('City'),
                  state: t('State / Province'),
                  postalCode: t('Postal Code'),
                  country: t('Country'),
                }}
              />
            </div>
            <Input label={t('Timezone')} required value={form.timezone} error={errors.timezone} onChange={(v) => handleFormChange('timezone', v)} placeholder="America/New_York" />
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-slate-500" />
            <h4 className="font-semibold text-ink">{t('Contact & admin')}</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label={t('Contact Phone')} required value={form.contactPhone} error={errors.contactPhone} onChange={(v) => handleFormChange('contactPhone', v)} icon={<Phone size={14} className="text-slate-400" />} />
            <Input label={t('Contact Email')} value={form.contactEmail} error={errors.contactEmail} onChange={(v) => handleFormChange('contactEmail', v)} icon={<Mail size={14} className="text-slate-400" />} />
            <Input label={t('Front Desk Contact')} value={form.details.contactName} onChange={(v) => handleDetailChange('contactName', v)} />
            <Input label={t('Services Offered')} value={form.details.services} onChange={(v) => handleDetailChange('services', v)} placeholder={t('Primary Care, Labs, Imaging')} />
            <Input label={t('Billing NPI')} value={form.details.billingNpi} onChange={(v) => handleDetailChange('billingNpi', v)} placeholder={t('For claims and referrals')} />
            <Input label={t('Billing Tax ID')} value={form.details.billingTaxId} onChange={(v) => handleDetailChange('billingTaxId', v)} placeholder={t('EIN for the site')} />
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-slate-500" />
            <h4 className="font-semibold text-ink">{t('Directions & notes')}</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextArea label={t('Patient Directions')} value={form.details.directions} onChange={(v) => handleDetailChange('directions', v)} />
            <TextArea label={t('Access & Parking')} value={form.details.parkingInfo} onChange={(v) => handleDetailChange('parkingInfo', v)} />
            <TextArea label={t('Telemed / Special Instructions')} value={form.details.accessInstructions} onChange={(v) => handleDetailChange('accessInstructions', v)} />
            <TextArea label={t('Internal Notes')} value={form.details.notes} onChange={(v) => handleDetailChange('notes', v)} />
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Clock3 size={16} className="text-slate-500" />
            <h4 className="font-semibold text-ink">{t('Availability')}</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Toggle label={t('Telemedicine Available')} value={form.telemedEnabled} onChange={(v) => handleFormChange('telemedEnabled', v)} />
            <Toggle label={t('Primary Location')} value={form.isPrimary} onChange={(v) => handleFormChange('isPrimary', v)} helper={t('Sets this as the default site for scheduling')} />
            <Toggle label={t('Active Location')} value={form.active} onChange={(v) => handleFormChange('active', v)} helper={t('Disable inactive business locations without deleting history')} />
          </div>
          <OpeningHoursEditor t={t} value={form.openingHours} onChange={(next) => handleFormChange('openingHours', next)} error={errors.openingHours} />
        </div>

        {!isNew && selectedLocation ? (
          <LocationStaffAssignmentPanel
            t={t}
            location={selectedLocation}
            staff={staffAssignments}
            saving={staffSaving}
            onSave={(userIds) => saveStaffAssignments(selectedLocation.id, userIds)}
          />
        ) : null}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate('/settings/locations')} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">
            {t('Cancel')}
          </button>
          <button type="submit" className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft disabled:opacity-60" disabled={saving}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {saving ? t('Saving...') : t('Save')}
          </button>
        </div>
      </form>
    </div>
  );
}

function OpeningHoursEditor({
  value,
  onChange,
  error,
  t,
}: {
  value: OpeningHours;
  onChange: (next: OpeningHours) => void;
  error?: string;
  t: (key: string) => string;
}) {
  const setDay = (day: DayKey, next: Partial<DaySchedule>) => {
    onChange({
      ...value,
      [day]: { ...value[day], ...next },
    });
  };

  const setPreset = (preset: 'weekdays' | 'always' | 'closed') => {
    if (preset === 'closed') {
      onChange(closedHours());
      return;
    }
    if (preset === 'always') {
      const next = closedHours();
      dayKeys.forEach((day) => {
        next[day] = { open: true, start: '00:00', end: '23:59' };
      });
      onChange(next);
      return;
    }
    const next = closedHours();
    dayKeys.forEach((day) => {
      if (['saturday', 'sunday'].includes(day)) return;
      next[day] = { open: true, start: '09:00', end: '17:00' };
    });
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <PresetButton label={t('Weekdays 9-5')} onClick={() => setPreset('weekdays')} />
        <PresetButton label={t('24/7')} onClick={() => setPreset('always')} />
        <PresetButton label={t('Mark closed')} onClick={() => setPreset('closed')} />
      </div>

      <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
        {dayKeys.map((day) => {
          const slot = value[day] || makeDay();
          return (
            <div key={day} className="grid grid-cols-12 gap-3 items-center px-4 py-3">
              <div className="col-span-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-ink"
                  checked={slot.open}
                  onChange={(e) => setDay(day, { open: e.target.checked, start: slot.start || '09:00', end: slot.end || '17:00' })}
                />
                <span className="font-medium text-ink">{dayLabels[day]}</span>
              </div>
              <div className="col-span-4">
                <input
                  type="time"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white disabled:bg-slate-50"
                  value={slot.start || ''}
                  disabled={!slot.open}
                  onChange={(e) => setDay(day, { start: e.target.value || null })}
                />
              </div>
              <div className="col-span-4">
                <input
                  type="time"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white disabled:bg-slate-50"
                  value={slot.end || ''}
                  disabled={!slot.open}
                  onChange={(e) => setDay(day, { end: e.target.value || null })}
                />
              </div>
              <div className="col-span-1 text-xs text-slate-500 text-right">{slot.open ? t('Open') : t('Closed')}</div>
            </div>
          );
        })}
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function LocationStaffAssignmentPanel({
  t,
  location,
  staff,
  saving,
  onSave,
}: {
  t: (key: string) => string;
  location: LocationApi;
  staff: LocationStaffAssignment[];
  saving: boolean;
  onSave: (userIds: string[]) => Promise<void>;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedIds(staff.filter((user) => user.assigned && !user.canAccessAllLocations).map((user) => user.id));
  }, [staff]);

  const assignedAllAccess = staff.filter((user) => user.canAccessAllLocations);
  const assignable = staff.filter((user) => !user.canAccessAllLocations);

  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-slate-500" />
          <div>
            <h4 className="font-semibold text-ink">{t('Location Staff Assignment')}</h4>
            <p className="text-xs text-slate-500">{t('Assign employees to this business location.')}</p>
          </div>
        </div>
        <button
          type="button"
          className="rounded-lg bg-ink px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
          disabled={saving || location.active === false}
          onClick={() => void onSave(selectedIds)}
        >
          {saving ? t('Saving...') : t('Save staff assignment')}
        </button>
      </div>
      {location.active === false ? (
        <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t('Reactivate this location before changing staff assignment.')}
        </div>
      ) : null}
      {assignedAllAccess.length ? (
        <div className="rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          {assignedAllAccess.length} {t('employee(s) can access all locations and do not need direct assignment.')}
        </div>
      ) : null}
      <div className="grid gap-2 md:grid-cols-2">
        {assignable.map((user) => (
          <label key={user.id} className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${user.active ? 'border-slate-100 bg-white' : 'border-slate-100 bg-slate-50 text-slate-400'}`}>
            <input
              type="checkbox"
              className="mt-1 accent-ink"
              disabled={!user.active || location.active === false}
              checked={selectedIds.includes(user.id)}
              onChange={(event) => {
                setSelectedIds((current) =>
                  event.target.checked
                    ? Array.from(new Set([...current, user.id]))
                    : current.filter((id) => id !== user.id),
                );
              }}
            />
            <span>
              <span className="block font-medium text-ink">{user.firstName} {user.lastName}</span>
              <span className="block text-xs text-slate-500">{user.email} · {t(user.role)} {user.defaultLocationId === location.id ? `· ${t('Default')}` : ''}</span>
            </span>
          </label>
        ))}
      </div>
      {!staff.length ? <p className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-500">{t('No employees available.')}</p> : null}
    </div>
  );
}

function PresetButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="px-3 py-1.5 text-xs rounded-full border border-slate-200 bg-white hover:bg-slate-50">
      {label}
    </button>
  );
}

function Input({
  label,
  value,
  onChange,
  required,
  error,
  placeholder,
  icon,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
  icon?: ReactNode;
  autoComplete?: string;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-600 font-medium flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <div className={`flex items-center gap-2 w-full border rounded-lg px-3 py-2 bg-white ${error ? 'border-rose-300' : 'border-slate-200'} focus-within:ring-2 focus-within:ring-ink/10`}>
        {icon}
        <input
          className="w-full outline-none text-sm bg-transparent"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
      </div>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-600 font-medium">{label}</span>
      <textarea
        className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-sm resize-none h-20 focus:ring-2 focus:ring-ink/10 outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function Toggle({
  label,
  value,
  onChange,
  helper,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  helper?: string;
}) {
  return (
    <label className="flex items-start gap-3 text-sm cursor-pointer border border-slate-200 rounded-lg px-3 py-2 bg-white">
      <input type="checkbox" className="mt-1 accent-ink" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <div>
        <span className="text-slate-800 font-medium">{label}</span>
        {helper && <p className="text-xs text-slate-500">{helper}</p>}
      </div>
    </label>
  );
}
